import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { isValidCSharpInt } from 'src/app/common-components/helpers/csharp-max-integer-helper';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { DateTimeFilterFormatPipe } from 'src/app/pipes/datetime-filter-format-pipe/datetime-filter-format-pipe';
import { DeleteCallRecordingVariables } from '../../active-calls/queries/generated/DeleteCallRecording';
import { CallLogsFilterOptions } from '../models/call-logs-filter-option';
import { CallLogsRegisterOptions } from '../models/call-logs-register-options';
import { getActiveCallLogsQuery } from '../queries/active-call-logs.graphql';
import { getCallLogsQuery } from '../queries/call-logs.graphql';
import { deleteCallRecordingMutation } from '../queries/delete-call-recording.graphql';
import { GetActiveCallLogs, GetActiveCallLogsVariables } from '../queries/generated/GetActiveCallLogs';
import { GetCallLogs, GetCallLogsVariables } from '../queries/generated/GetCallLogs';

@Injectable({ providedIn: 'root' })
export class CallLogsRegisterApiService {
   private dateTimePipe: DateTimeFilterFormatPipe;

   constructor(private apiService: LcsApiService) {
      this.dateTimePipe = new DateTimeFilterFormatPipe();
   }

   static calculateDuration(startTime: Date, endTime: Date | null): number {
      if (endTime == null) {
         const endTimeString = new Date().toLocaleString('en-US', { timeZone: 'UTC' });
         endTime = new Date(endTimeString);
      } else {
         endTime = new Date(endTime);
      }
      startTime = new Date(startTime);
      const startTimestamp = startTime.getTime();
      const endTimestamp = endTime.getTime();
      const duration = endTimestamp - startTimestamp;
      const durationInSeconds = duration / 1000;
      return durationInSeconds;
   }

   getCallLogs(
      endCursor: string | null,
      filters: CallLogsFilterOptions[] | null,
      quickFilters: CallLogsRegisterOptions | null,
      orderingOptions: OrderingOption[] | null
   ): Observable<ExecutionResult<GetCallLogs>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertCallLogsFilterOption.bind(this)),
              }
            : { filterOptions: [] };
      if (quickFilters) {
         const subExpression: FilterExpression = { filterOptions: [] };
         if (quickFilters.startDate) {
            const startUtc = new Date(quickFilters.startDate).toISOString();
            filterBuilderFilterExpression.filterOptions.push({
               field: 'startTime',
               operation: FilterOperations.GreaterThanOrEqualTo,
               value: this.dateTimePipe.transformUTC(startUtc),
            });
         }

         if (quickFilters.endDate) {
            const endUtc = new Date(quickFilters.endDate).toISOString();
            filterBuilderFilterExpression.filterOptions.push({
               field: 'endTime',
               operation: FilterOperations.LessThanOrEqualTo,
               value: this.dateTimePipe.transformUTC(endUtc),
            });
         }

         if (quickFilters.callDirection) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'callDirectionTypeId',
               operation: FilterOperations.EqualTo,
               value: quickFilters.callDirection,
            });
         }

         if (quickFilters.callDisposition) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'callDispositionTypeId',
               operation: FilterOperations.EqualTo,
               value: quickFilters.callDisposition,
            });
         }
         //TODO Add call duration filter
         // if (quickFilters.fromDuration) {
         //    filterBuilderFilterExpression.filterOptions.push({
         //    field: 'callDuration',
         //    operation: FilterOperations.GreaterThanOrEqualTo,
         //    value: quickFilters.fromDuration,
         //    });
         // }
         // if (quickFilters.toDuration) {
         //    filterBuilderFilterExpression.filterOptions.push({
         //    field: 'callDuration',
         //    operation: FilterOperations.LessThanOrEqualTo,
         //    value: quickFilters.toDuration,
         //    });
         // }
         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim().replace(/-/g, '');
            subExpression.logicalOperator = LogicalOperators.Or;

            subExpression.filterOptions.push({
               field: 'sourceExtension.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });

            subExpression.filterOptions.push({
               field: 'destinationExtension.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });

            const extensionIdOrNumberRegex = /\d+/;

            const numericSearchText = searchText.match(extensionIdOrNumberRegex)?.[0];
            if (numericSearchText) {
               subExpression.filterOptions.push({
                  field: 'destinationNumber',
                  operation: FilterOperations.Contains,
                  value: numericSearchText,
               });
               subExpression.filterOptions.push({
                  field: 'sourceNumber',
                  operation: FilterOperations.Contains,
                  value: numericSearchText,
               });
               if (isValidCSharpInt(+searchText) && numericSearchText) {
                  subExpression.filterOptions.push({
                     field: 'sourceExtension.extensionId',
                     operation: FilterOperations.Contains,
                     value: numericSearchText,
                  });
                  subExpression.filterOptions.push({
                     field: 'sourceExtension.number',
                     operation: FilterOperations.Contains,
                     value: numericSearchText,
                  });
                  subExpression.filterOptions.push({
                     field: 'destinationExtension.extensionId',
                     operation: FilterOperations.Contains,
                     value: numericSearchText,
                  });
                  subExpression.filterOptions.push({
                     field: 'destinationExtension.Number',
                     operation: FilterOperations.Contains,
                     value: numericSearchText,
                  });
               }
            }
         }
         filterBuilderFilterExpression.subExpressions = [subExpression];
      }

      const completedVars = this.getCallLogsVariables(endCursor, filterBuilderFilterExpression, orderingOptions);

      const completed$ = this.apiService.query<GetCallLogs, GetCallLogsVariables>({
         query: getCallLogsQuery,
         variables: completedVars,
      });

      if (endCursor) {
         return completed$;
      }

      const activeVars = this.getCallLogsVariables(
         null, // don't paginate active calls
         this.removeEndTimeFilters(filterBuilderFilterExpression),
         orderingOptions
      );

      const active$ = this.apiService.query<GetActiveCallLogs, GetActiveCallLogsVariables>({
         query: getActiveCallLogsQuery,
         variables: activeVars,
      });

      return forkJoin({
         completed: completed$,
         active: active$,
      }).pipe(
         map(({ completed, active }) => {
            const completedItems = completed.data?.callDetailsConnection.items ?? [];
            const activeItems = active.data?.activeCallsConnection.items ?? [];

            // UNION + de-dupe by CallDetailId
            const merged = new Map<number, any>();
            [...completedItems, ...activeItems].forEach((c) => merged.set(c.callDetailId, c));

            const compare = this.buildOrderingComparator(orderingOptions);
            const items = Array.from(merged.values()).sort(compare);

            return {
               ...completed,
               data: {
                  callDetailsConnection: {
                     ...completed.data!.callDetailsConnection,
                     items,
                     totalCount: items.length,
                  },
               },
            };
         })
      );
   }

   /**
    * Builds the Ordering options in the same way the api would, since we are stitching 2 requests together
    */
   private buildOrderingComparator(orderingOptions: OrderingOption[] | null): (a: any, b: any) => number {
      const activeOptions = (orderingOptions ?? []).filter((option) => option.direction !== OrderingDirections.NotSet);

      const options =
         activeOptions.length > 0 ? activeOptions : [{ field: 'startTime', direction: OrderingDirections.Desc }];

      return (a: any, b: any) => {
         for (const option of options) {
            const aValue = this.getFieldValue(a, option.field);
            const bValue = this.getFieldValue(b, option.field);
            const comparison = this.compareValues(aValue, bValue);

            if (comparison !== 0) {
               return option.direction === OrderingDirections.Asc ? comparison : -comparison;
            }
         }
         return 0;
      };
   }

   private getFieldValue(item: any, field: string): unknown {
      return field.split('.').reduce((value: any, key) => (value == null ? value : value[key]), item);
   }

   private compareValues(a: unknown, b: unknown): number {
      if (a == null && b == null) {
         return 0;
      }
      if (a == null) {
         return -1;
      }
      if (b == null) {
         return 1;
      }

      if (a instanceof Date || b instanceof Date || this.looksLikeDate(a) || this.looksLikeDate(b)) {
         return new Date(a as string).getTime() - new Date(b as string).getTime();
      }
      if (typeof a === 'number' && typeof b === 'number') {
         return a - b;
      }
      if (typeof a === 'boolean' && typeof b === 'boolean') {
         return a === b ? 0 : a ? 1 : -1;
      }
      return String(a).localeCompare(String(b));
   }

   private looksLikeDate(value: unknown): boolean {
      return typeof value === 'string' && !isNaN(Date.parse(value));
   }

   private removeEndTimeFilters(filters: FilterExpression | null): FilterExpression | null {
      if (!filters) {
         return null;
      }

      const filteredSubExpressions =
         filters.subExpressions
            ?.map((sub) => this.removeEndTimeFilters(sub))
            .filter((sub): sub is FilterExpression => sub !== null) ?? // <- type guard
         [];

      return {
         ...filters,
         filterOptions: filters.filterOptions.filter((f) => f.field !== 'endTime'),
         subExpressions: filteredSubExpressions,
      };
   }

   deleteCallRecording(callRecordingId: number): Observable<ExecutionResult<void>> {
      return this.apiService.query<void, DeleteCallRecordingVariables>({
         query: deleteCallRecordingMutation,
         variables: {
            callRecordingId,
         },
      });
   }

   private getCallLogsVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetCallLogsVariables {
      const variables: GetCallLogsVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters && filterBuilderFilters.filterOptions.length > 0) {
         const extensionIdExpressions: FilterOption[] = [];

         //Adding Or for multiple ids
         for (const expr of filterBuilderFilters.filterOptions) {
            if (expr.field === 'CallSegments.ExtensionId') {
               extensionIdExpressions.push(expr);
            }
         }
         const remainingFilterOptions = filterBuilderFilters.filterOptions.filter(
            (expr) => expr.field !== 'CallSegments.ExtensionId'
         );

         if (extensionIdExpressions.length > 0) {
            expressions.push({
               filterOptions: extensionIdExpressions,
               logicalOperator: LogicalOperators.Or,
               subExpressions: [],
            });
         }

         return serializeFilterExpression({
            filterOptions: remainingFilterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }

   private convertCallLogsFilterOption(option: CallLogsFilterOptions): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: CallLogsFilterOptions): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'callLogs':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.callDetailId;
                 })
               : option.value?.callDetailId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }
}
